// src/pages/about/About.tsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  useMediaQuery,
  Link as MuiLink,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Chip,
  Button,
  Tooltip,
  Snackbar,
  Alert,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Card,
  CardContent,
  LinearProgress,
  alpha,
  Fade,
  Zoom,
} from "@mui/material";
import {
  InfoOutlined as InfoIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  Computer as ComputerIcon,
  Public as PublicIcon,
  Language as LanguageIcon,
  AccessTime as TimeIcon,
  History as HistoryIcon,
  SupportAgent as SupportIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  NetworkCheck as NetworkIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  CloudQueue as CloudIcon,
  Analytics as AnalyticsIcon,
  Code as CodeIcon,
  Build as BuildIcon,
} from "@mui/icons-material";
import { useTheme, styled, keyframes } from "@mui/material/styles";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";
import { getUserInfoFromToken } from "../../utils/Main";

// Advanced Styled Components
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const GlassCard = styled(Card)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.85)
      : alpha("#ffffff", 0.95),
  backdropFilter: "saturate(180%) blur(20px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: 20,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${fadeInUp} 0.6s ease-out`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 16px 48px rgba(0, 0, 0, 0.5)"
        : "0 16px 48px rgba(0, 0, 0, 0.12)",
  },
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 20,
  padding: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.9)
      : alpha("#ffffff", 0.95),
  backdropFilter: "saturate(180%) blur(20px)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  animation: `${fadeInUp} 0.6s ease-out`,
  "&:hover": {
    transform: "translateY(-6px) scale(1.02)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 20px 60px rgba(0, 0, 0, 0.5)"
        : "0 20px 60px rgba(0, 0, 0, 0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.8)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.8)})`,
    borderRadius: "20px 20px 0 0",
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: 20,
  display: "grid",
  placeItems: "center",
  position: "relative",
  transition: "all 0.3s ease",
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.15)})`,
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: 20,
    padding: 2,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.3)})`,
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover::after": {
    opacity: 1,
  },
}));

const StatusBadge = styled(Chip)(() => ({
  animation: `${pulse} 2s ease-in-out infinite`,
  fontWeight: 600,
  fontSize: "0.75rem",
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.938rem",
  minHeight: 48,
  borderRadius: 12,
  marginRight: theme.spacing(1),
  transition: "all 0.3s ease",
  "&.Mui-selected": {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.15)})`,
    color: theme.palette.primary.main,
  },
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.08),
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`about-tabpanel-${index}`}
      aria-labelledby={`about-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const About: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentYear = new Date().getFullYear();
  const [tabValue, setTabValue] = useState(0);

  // ✅ Read from Vite env safely (no process.env)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  const viteEnv: Record<string, string | undefined> =
    (typeof import.meta !== "undefined" && (import.meta as any).env) || {};

  const appVersion =
    viteEnv.VITE_APP_VERSION || "v1.2.0";
  const buildSha = 
    viteEnv.VITE_APP_GIT_SHA || "";
  const buildDate =
    viteEnv.VITE_APP_BUILD_DATE || "";
  const environment =
    viteEnv.MODE || viteEnv.VITE_NODE_ENV || "production";

  const user = getUserInfoFromToken();
  const workspaceName = user?.workspaceName ?? "Unknown Workspace";

  // Derive runtime diagnostics (guard navigator for SSR safety)
  const diagnostics = useMemo(() => {
    const ua =
      typeof navigator !== "undefined" ? navigator.userAgent : "N/A";
    const locale =
      typeof navigator !== "undefined" ? navigator.language : "N/A";
    const timeZone =
      typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : "N/A";

    return {
      version: appVersion,
      environment,
      buildSha: buildSha || "N/A",
      buildDate: buildDate || "N/A",
      workspace: workspaceName,
      userAgent: ua,
      locale,
      timeZone,
    };
  }, [appVersion, environment, buildSha, buildDate, workspaceName]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({ open: false, message: "", severity: "success" });
  interface PerformanceMemory {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  }

  interface NetworkConnection {
    effectiveType: string;
    downlink: number;
    rtt: number;
  }

  const [systemMetrics, setSystemMetrics] = useState({
    loadTime: performance.timing ? performance.timing.loadEventEnd - performance.timing.navigationStart : 0,
    memory: (performance as Performance & { memory?: PerformanceMemory }).memory ? {
      used: Math.round(((performance as Performance & { memory?: PerformanceMemory }).memory!.usedJSHeapSize / 1048576)),
      total: Math.round(((performance as Performance & { memory?: PerformanceMemory }).memory!.totalJSHeapSize / 1048576)),
      limit: Math.round(((performance as Performance & { memory?: PerformanceMemory }).memory!.jsHeapSizeLimit / 1048576)),
    } : null,
    connection: (navigator as Navigator & { connection?: NetworkConnection }).connection ? {
      effectiveType: (navigator as Navigator & { connection?: NetworkConnection }).connection!.effectiveType,
      downlink: (navigator as Navigator & { connection?: NetworkConnection }).connection!.downlink,
      rtt: (navigator as Navigator & { connection?: NetworkConnection }).connection!.rtt,
    } : null,
    timestamp: new Date().toISOString(),
  });

  const copy = async (text: string, label?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: `${label ?? "Copied"} to clipboard`,
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Copy failed",
        severity: "error",
      });
    }
  };

  const refreshMetrics = () => {
    setSystemMetrics({
      loadTime: performance.timing ? performance.timing.loadEventEnd - performance.timing.navigationStart : 0,
      memory: (performance as Performance & { memory?: PerformanceMemory }).memory ? {
        used: Math.round(((performance as Performance & { memory?: PerformanceMemory }).memory!.usedJSHeapSize / 1048576)),
        total: Math.round(((performance as Performance & { memory?: PerformanceMemory }).memory!.totalJSHeapSize / 1048576)),
        limit: Math.round(((performance as Performance & { memory?: PerformanceMemory }).memory!.jsHeapSizeLimit / 1048576)),
      } : null,
      connection: (navigator as Navigator & { connection?: NetworkConnection }).connection ? {
        effectiveType: (navigator as Navigator & { connection?: NetworkConnection }).connection!.effectiveType,
        downlink: (navigator as Navigator & { connection?: NetworkConnection }).connection!.downlink,
        rtt: (navigator as Navigator & { connection?: NetworkConnection }).connection!.rtt,
      } : null,
      timestamp: new Date().toISOString(),
    });
    setSnackbar({
      open: true,
      message: "Metrics refreshed",
      severity: "success",
    });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getMemoryPercentage = () => {
    if (!systemMetrics.memory) return 0;
    return Math.round((systemMetrics.memory.used / systemMetrics.memory.limit) * 100);
  };

  const getSystemStatus = () => {
    const memoryPct = getMemoryPercentage();
    if (memoryPct > 80) return { status: "warning", label: "High Memory Usage", color: "warning" as const };
    if (memoryPct > 60) return { status: "info", label: "Normal", color: "info" as const };
    return { status: "success", label: "Healthy", color: "success" as const };
  };

  const systemStatus = getSystemStatus();

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "radial-gradient(1200px 600px at 10% -10%, rgba(93,176,255,0.08), transparent), linear-gradient(180deg, rgba(255,255,255,0.02), transparent)"
            : "radial-gradient(1200px 600px at 10% -10%, rgba(93,176,255,0.10), transparent), linear-gradient(180deg, #F7FAFF, #ECF3FF)",
      }}
    >
      {/* Top actions row */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <StatusBadge
            icon={<CheckCircleIcon />}
            label={systemStatus.label}
            color={systemStatus.color}
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            System {systemStatus.label}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Tooltip title="Refresh Metrics">
            <IconButton onClick={refreshMetrics} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        <Tooltip title="Contact Support">
          <Button
            variant="contained"
            startIcon={<SupportIcon />}
            component={MuiLink}
            href="mailto:support@alseirafi.com"
              size={isMobile ? "small" : "medium"}
          >
            Support
          </Button>
        </Tooltip>
        <Button
          variant="outlined"
          endIcon={<OpenInNewIcon />}
          component={MuiLink}
          href="/terms"
          underline="none"
            size={isMobile ? "small" : "medium"}
        >
          Terms
        </Button>
        <Button
          variant="outlined"
          endIcon={<OpenInNewIcon />}
          component={MuiLink}
          href="/privacy"
          underline="none"
            size={isMobile ? "small" : "medium"}
        >
          Privacy
        </Button>
        </Stack>
      </Box>

      {/* Hero Section */}
      <Fade in timeout={800}>
        <GlassCard
        sx={{
            mb: 4,
            textAlign: "center",
          px: { xs: 2, md: 4 },
          py: { xs: 4, md: 6 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
              filter: "blur(60px)",
            }}
          />
          <Stack alignItems="center" spacing={2}>
            <Zoom in timeout={1000}>
              <IconContainer>
          <InfoIcon fontSize="large" color="primary" />
              </IconContainer>
            </Zoom>
          <Typography variant={isMobile ? "h4" : "h3"} fontWeight={800}>
            About NOVERA ERP SYSTEM
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" maxWidth={720}>
            Comprehensive Enterprise Resource Planning system for managing teams, tasks, workspaces, and business operations efficiently—clean UI, powerful features, and a focus on productivity.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            mt={1}
            flexWrap="wrap"
            justifyContent="center"
          >
              <Chip label={`Version ${appVersion}`} size="small" color="primary" />
            <Chip label={environment.toUpperCase()} size="small" color="default" />
            {buildSha && <Chip label={`Build ${buildSha.slice(0, 7)}`} size="small" />}
          </Stack>
        </Stack>
        </GlassCard>
      </Fade>

      {/* Tabs Navigation */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <StyledTab icon={<DashboardIcon />} iconPosition="start" label="Overview" />
          <StyledTab icon={<SettingsIcon />} iconPosition="start" label="System Info" />
          <StyledTab icon={<SpeedIcon />} iconPosition="start" label="Performance" />
          <StyledTab icon={<HistoryIcon />} iconPosition="start" label="Changelog" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Quick Stats */}
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard>
              <Stack spacing={2}>
                <IconContainer>
                  <CloudIcon color="primary" />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    Cloud
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hosted Infrastructure
                  </Typography>
                </Box>
              </Stack>
            </FeatureCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard>
              <Stack spacing={2}>
                <IconContainer>
                  <SecurityIcon color="primary" />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    Secure
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enterprise Security
                  </Typography>
                </Box>
              </Stack>
            </FeatureCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard>
              <Stack spacing={2}>
                <IconContainer>
                  <AnalyticsIcon color="primary" />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    Analytics
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Real-time Insights
                  </Typography>
                </Box>
              </Stack>
            </FeatureCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard>
              <Stack spacing={2}>
                <IconContainer>
                  <BuildIcon color="primary" />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    Flexible
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customizable Modules
                  </Typography>
                </Box>
              </Stack>
            </FeatureCard>
          </Grid>

          {/* Application Info */}
          <Grid item xs={12} md={6}>
            <GlassCard>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <CodeIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    Application Details
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>Version</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip label={appVersion} size="small" color="primary" />
                      <Tooltip title="Copy version">
                        <IconButton size="small" onClick={() => copy(appVersion, "Version")}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>Environment</Typography>
                    <Chip label={environment.toUpperCase()} size="small" />
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>Build SHA</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={buildSha ? buildSha.slice(0, 7) : "N/A"} size="small" />
                      {buildDate && (
                        <Chip icon={<TimeIcon />} label={buildDate} size="small" variant="outlined" />
                      )}
                    </Stack>
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>Licensed for</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip label={workspaceName} size="small" color="primary" variant="outlined" />
                      <Tooltip title="Copy workspace name">
                        <IconButton size="small" onClick={() => copy(workspaceName, "Workspace name")}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* System Status */}
          <Grid item xs={12} md={6}>
            <GlassCard>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <DashboardIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    System Status
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Overall Health</Typography>
                      <StatusBadge label={systemStatus.label} color={systemStatus.color} size="small" />
                    </Stack>
                  </Box>
                  {systemMetrics.memory && (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Memory Usage</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {systemMetrics.memory.used}MB / {systemMetrics.memory.limit}MB
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={getMemoryPercentage()}
                        color={getMemoryPercentage() > 80 ? "error" : getMemoryPercentage() > 60 ? "warning" : "success"}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  )}
                  {systemMetrics.connection && (
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Network Type</Typography>
                        <Chip label={systemMetrics.connection.effectiveType.toUpperCase()} size="small" />
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Download Speed</Typography>
                        <Typography variant="body2">{systemMetrics.connection.downlink} Mbps</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Latency</Typography>
                        <Typography variant="body2">{systemMetrics.connection.rtt}ms</Typography>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
      <Grid container spacing={3}>
          {/* Application & License */}
        <Grid item xs={12} md={6}>
            <GlassCard>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <CodeIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>
              Application
            </Typography>
                </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography>Version</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip label={appVersion} size="small" />
                  <Tooltip title="Copy version">
                    <IconButton size="small" onClick={() => copy(appVersion, "Version")}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography>Environment</Typography>
                <Chip label={environment.toUpperCase()} size="small" />
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography>Build</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={buildSha ? buildSha.slice(0, 7) : "N/A"} size="small" />
                  {buildDate && (
                        <Chip icon={<TimeIcon />} label={buildDate} size="small" variant="outlined" />
                  )}
                </Stack>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography>Licensed for</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip label={workspaceName} size="small" color="primary" />
                  <Tooltip title="Copy workspace name">
                        <IconButton size="small" onClick={() => copy(workspaceName, "Workspace name")}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
              </CardContent>
            </GlassCard>
        </Grid>

        {/* Diagnostics */}
        <Grid item xs={12} md={6}>
            <GlassCard>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <ComputerIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>
              Diagnostics
            </Typography>
                </Stack>
            <Divider sx={{ mb: 2 }} />
            <List dense disablePadding>
              <ListItem
                secondaryAction={
                  <Tooltip title="Copy user agent">
                        <IconButton edge="end" onClick={() => copy(diagnostics.userAgent, "User Agent")}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                    <ComputerIcon fontSize="small" style={{ marginRight: 8, color: theme.palette.primary.main }} />
                <ListItemText primary="User Agent" secondary={diagnostics.userAgent} />
              </ListItem>
              <ListItem>
                    <LanguageIcon fontSize="small" style={{ marginRight: 8, color: theme.palette.primary.main }} />
                <ListItemText primary="Locale" secondary={diagnostics.locale} />
              </ListItem>
              <ListItem>
                    <PublicIcon fontSize="small" style={{ marginRight: 8, color: theme.palette.primary.main }} />
                <ListItemText primary="Time Zone" secondary={diagnostics.timeZone} />
              </ListItem>
            </List>
                <Stack direction="row" justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => copy(JSON.stringify(diagnostics, null, 2), "Diagnostics")}
              >
                    Copy All
              </Button>
            </Stack>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {/* Performance Metrics */}
        <Grid item xs={12}>
            <GlassCard>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <SpeedIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>
                      Performance Metrics
                    </Typography>
                  </Stack>
                  <Button startIcon={<RefreshIcon />} onClick={refreshMetrics} size="small">
                    Refresh
                  </Button>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  {systemMetrics.memory && (
                    <Grid item xs={12} md={6}>
                      <Box mb={2}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                          <MemoryIcon color="primary" />
                          <Typography variant="subtitle1" fontWeight={600}>
                            Memory Usage
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            Used: {systemMetrics.memory.used} MB
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Limit: {systemMetrics.memory.limit} MB
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={getMemoryPercentage()}
                          color={getMemoryPercentage() > 80 ? "error" : getMemoryPercentage() > 60 ? "warning" : "success"}
                          sx={{ height: 10, borderRadius: 5 }}
                        />
                        <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                          {getMemoryPercentage()}% utilized
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Box mb={2}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <TimeIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Load Time
                        </Typography>
                      </Stack>
                      <Typography variant="h4" fontWeight={700} color="primary">
                        {systemMetrics.loadTime}ms
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Page load performance
                      </Typography>
                    </Box>
                  </Grid>
                  {systemMetrics.connection && (
                    <>
                      <Grid item xs={12} md={4}>
                        <FeatureCard>
                          <Stack spacing={1}>
                            <NetworkIcon color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Network Type
                            </Typography>
                            <Chip label={systemMetrics.connection.effectiveType.toUpperCase()} size="small" color="primary" />
                          </Stack>
                        </FeatureCard>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FeatureCard>
                          <Stack spacing={1}>
                            <SpeedIcon color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Download Speed
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="primary">
                              {systemMetrics.connection.downlink} Mbps
                            </Typography>
                          </Stack>
                        </FeatureCard>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FeatureCard>
                          <Stack spacing={1}>
                            <TimeIcon color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Latency
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="primary">
                              {systemMetrics.connection.rtt}ms
                            </Typography>
                          </Stack>
                        </FeatureCard>
                      </Grid>
                    </>
                  )}
                </Grid>
                {systemMetrics.timestamp && (
                  <Typography variant="caption" color="text.secondary" mt={2} display="block">
                    Last updated: {new Date(systemMetrics.timestamp).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <GlassCard>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <HistoryIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Changelog
              </Typography>
            </Stack>
                <Divider sx={{ mb: 2 }} />

                <Accordion disableGutters defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label="v1.2.0" size="small" color="primary" />
                  <Typography fontWeight={600}>July 2025</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  <ListItem>
                        <ListItemText
                          primary="Enhanced About Page"
                          secondary="Added advanced UI with tabs, performance metrics, and system diagnostics"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Markdown Editor"
                          secondary="Added markdown editor support for rich text editing"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Dark Mode"
                          secondary="Complete dark mode support across all components"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Navigation Improvements"
                          secondary="Enhanced side navigation with better organization"
                        />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

                <Accordion disableGutters sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label="v1.1.0" size="small" />
                  <Typography fontWeight={600}>June 2025</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  <ListItem>
                        <ListItemText primary="Workspace Tree View" secondary="Introduced hierarchical workspace visualization" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Resizable Panels" secondary="Added resizable side panels for better layout control" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Inline Editing" secondary="Implemented inline editing capabilities across modules" />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label="v1.0.0" size="small" />
                  <Typography fontWeight={600}>May 2025</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  <ListItem>
                        <ListItemText primary="Initial Release" secondary="Core features: Tasks, Notebooks, and User Roles management" />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Footer */}
      <Fade in timeout={1200}>
        <Box
          sx={{
            mt: 4,
            p: 3,
            textAlign: "center",
            borderRadius: 3,
            background:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.6)
                : alpha("#ffffff", 0.7),
            backdropFilter: "blur(10px)",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={3} justifyContent="center" mb={2}>
            <MuiLink href="/terms" underline="hover" color="text.secondary" sx={{ "&:hover": { color: "primary.main" } }}>
                Terms of Service
              </MuiLink>
            <MuiLink href="/privacy" underline="hover" color="text.secondary" sx={{ "&:hover": { color: "primary.main" } }}>
                Privacy Policy
              </MuiLink>
            <MuiLink href="mailto:support@alseirafi.com" underline="hover" color="text.secondary" sx={{ "&:hover": { color: "primary.main" } }}>
                support@alseirafi.com
              </MuiLink>
            </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              &copy; {currentYear}{" "}
              <Box component="span" fontWeight="bold" color="primary.main">
                ALSEIRAFI GROUP
              </Box>
            . Crafted with ❤️ by{" "}
              <Box component="span" fontWeight="bold" color="primary.main">
                ALAA ALSEIRAFI
              </Box>
              .
            </Typography>
        </Box>
      </Fade>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default About;
